import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import { Chance } from 'chance';
import jwtDecode from 'jwt-decode';

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios, { setOrgIdInterceptor } from 'utils/axios';
import logger, { initClientLogger, resetClientLogger, setClientLoggerToken } from 'utils/logger';

const chance = new Chance();

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    userId: 0,
    email: null,
    clinicGrants: {},
    organizationMeta: {},
    userMetadata: {
        isProviderAccount: false,
        providerId: null,
        firstName: '',
        lastName: ''
    }
};

const verifyToken = (serviceToken) => {
    if (!serviceToken) {
        return false;
    }
    const decoded = jwtDecode(serviceToken);
    /**
     * Property 'exp' does not exist on type '<T = unknown>(token, options?: JwtDecodeOptions | undefined) => T'.
     */
    return decoded.exp > Date.now() / 1000;
};

const configureOrgIdInterceptor = (orgId) => {
    setOrgIdInterceptor(orgId);
    initClientLogger(orgId);
};

const setSession = (serviceToken) => {
    if (serviceToken) {
        const bearer = `Bearer ${serviceToken}`;
        localStorage.setItem('serviceToken', serviceToken);
        axios.defaults.headers.common.Authorization = bearer;
        setClientLoggerToken(bearer);
    } else {
        localStorage.removeItem('serviceToken');
        delete axios.defaults.headers.common.Authorization;
        setClientLoggerToken(null);
    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);

    const loadAccount = async () => {
        const response = await axios.get('/api/account/me');
        const { email, userId, clinicGrants, organizationMeta, userMetadata } = response.data;
        configureOrgIdInterceptor(organizationMeta.meta.id);
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                userId,
                email,
                clinicGrants,
                organizationMeta,
                userMetadata
            }
        });
    };

    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = window.localStorage.getItem('serviceToken');
                if (serviceToken && verifyToken(serviceToken)) {
                    setSession(serviceToken);
                    await loadAccount();
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                }
            } catch (err) {
                logger.error('account:init failed', { error: err?.message, code: err?.code });
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const login = async (email, password) => {
        const response = await axios.post('/api/account/login', { email, password });
        setSession(response.data.serviceToken);
        configureOrgIdInterceptor(response.data.organizationMeta.meta.id);
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                email: response.data.email,
                userId: response.data.userId,
                clinicGrants: response.data.clinicGrants,
                organizationMeta: response.data.organizationMeta,
                userMetadata: response.data.userMetadata
            }
        });
    };

    const loginWithGoogle = async (credential) => {
        const response = await axios.post('/api/account/login-google', { credential });
        setSession(response.data.serviceToken);
        configureOrgIdInterceptor(response.data.organizationMeta.meta.id);
        dispatch({
            type: LOGIN,
            payload: {
                isLoggedIn: true,
                email: response.data.email,
                userId: response.data.userId,
                clinicGrants: response.data.clinicGrants,
                organizationMeta: response.data.organizationMeta,
                userMetadata: response.data.userMetadata
            }
        });
    };

    const register = async (email, password, firstName, lastName) => {
        // todo: this flow need to be recode as it not verified
        const id = chance.bb_pin();
        const response = await axios.post('/api/account/register', {
            id,
            email,
            password,
            firstName,
            lastName
        });
        let users = response.data;

        if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
            const localUsers = window.localStorage.getItem('users');
            users = [
                ...JSON.parse(localUsers),
                {
                    id,
                    email,
                    password,
                    name: `${firstName} ${lastName}`
                }
            ];
        }

        window.localStorage.setItem('users', JSON.stringify(users));
    };

    const logout = () => {
        setSession(null);
        configureOrgIdInterceptor(0);
        resetClientLogger();
        dispatch({ type: LOGOUT });
    };

    const refreshAccount = async () => {
        const serviceToken = window.localStorage.getItem('serviceToken');
        // Same expiry check init() runs at load, applied mid-session: only logs out on a
        // missing/dead token, and callers gate this to neutral pages, so no work is lost.
        if (!serviceToken || !verifyToken(serviceToken)) {
            logout();
            return;
        }
        try {
            await loadAccount();
        } catch (err) {
            // Transient failure: keep current grants. The caller already advanced its staleness
            // clock, so this waits out the window rather than retrying on a flaky network.
            logger.warn('account:refresh failed', { error: err?.message, code: err?.code });
        }
    };

    const resetPassword = (email) => console.log(email);

    const updateProfile = () => {};

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider value={{ ...state, login, loginWithGoogle, logout, register, resetPassword, updateProfile, refreshAccount }}>
            {children}
        </JWTContext.Provider>
    );
};

JWTProvider.propTypes = {
    children: PropTypes.node
};

export default JWTContext;
